import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Check, X } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";

const TYPE_LABELS = {
  website: { de: "Websites", en: "Websites" },
  mobile: { de: "Mobile Apps", en: "Mobile Apps" },
  software: { de: "Software", en: "Software" },
  maintenance: { de: "Wartung", en: "Maintenance" },
  support: { de: "Support", en: "Support" },
  hosting: { de: "Hosting", en: "Hosting" },
  custom: { de: "Individuell", en: "Custom" },
};

export default function Packages() {
  const { t, lang, pickL } = useI18n();
  const { user } = useAuth();
  const nav = useNavigate();
  const de = lang === "de";
  const [packages, setPackages] = useState([]);
  const [type, setType] = useState(null);

  useEffect(() => { api.get("/packages").then((r) => setPackages(Array.isArray(r.data) ? r.data : [])).catch(() => {}); }, []);

  // Tabs are derived dynamically from the package types actually present.
  const activeTypes = Array.from(new Set(packages.map((p) => p.type).filter(Boolean)));
  const orderedTypes = [...Object.keys(TYPE_LABELS).filter((k) => activeTypes.includes(k)),
    ...activeTypes.filter((k) => !TYPE_LABELS[k])];
  useEffect(() => {
    if (orderedTypes.length && (type === null || !orderedTypes.includes(type))) setType(orderedTypes[0]);
  }, [packages]); // eslint-disable-line
  const label = (k) => (TYPE_LABELS[k] ? TYPE_LABELS[k][lang] : k.charAt(0).toUpperCase() + k.slice(1));
  const list = packages.filter((p) => p.type === type);

  const start = (id) => {
    const dest = `/checkout/package/${id}`;
    if (user) nav(dest); else nav(`/register?next=${encodeURIComponent(dest)}`);
  };

  return (
    <div>
      <Seo title={t("home.packagesTitle")} description={t("home.packagesSub")} />
      <PageHero eyebrow={t("nav.packages")} title={t("home.packagesTitle")} subtitle={t("home.packagesSub")} />
      <section className="container-x py-14">
        <div className="flex flex-wrap gap-2 mb-12 justify-center">
          {orderedTypes.map((k) => (
            <button key={k} onClick={() => setType(k)} data-testid={`pkg-type-${k}`}
              className={`rounded-full px-5 py-2 text-sm font-semibold border transition-colors ${type === k ? "bg-primary text-primary-foreground border-primary" : "border-border hover:border-primary"}`}>
              {label(k)}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {list.map((p, i) => (
            <Reveal key={p.id} delay={i * 0.05}>
              <div className={`surface p-8 h-full relative flex flex-col ${p.recommended ? "border-primary glow-orange" : ""}`} data-testid={`package-${i}`}>
                {(p.popular || p.recommended) && (
                  <span className="absolute -top-3 left-6 rounded-full bg-primary text-primary-foreground text-xs font-bold px-3 py-1">
                    {p.popular ? "Popular" : (de ? "Empfohlen" : "Recommended")}
                  </span>
                )}
                <h3 className="font-display font-bold text-xl">{pickL(p, "name")}</h3>
                <p className="text-sm text-muted-foreground mt-1">{pickL(p, "desc")}</p>
                <div className="mt-5 mb-6">
                  {p.price_discounted && <span className="text-muted-foreground line-through mr-2">{p.price}€</span>}
                  <span className="font-display text-4xl font-black">{p.price_discounted || p.price}€</span>
                  <span className="text-sm text-muted-foreground"> {p.billing === "monthly" ? "/ Monat" : ""} · {de ? "Gemäß § 19 UStG wird keine Umsatzsteuer berechnet." : "No VAT is charged pursuant to § 19 UStG."}</span>
                </div>
                <ul className="space-y-2.5 flex-1">
                  {(pickL(p, "features") || []).map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm"><Check size={16} className="text-primary shrink-0 mt-0.5" /> {f}</li>
                  ))}
                  {(pickL(p, "excluded") || []).map((f, j) => (
                    <li key={j} className="flex items-start gap-2 text-sm text-muted-foreground"><X size={16} className="shrink-0 mt-0.5" /> {f}</li>
                  ))}
                </ul>
                <div className="mt-6 text-xs text-muted-foreground">{de ? "Lieferzeit" : "Delivery"}: {p.delivery_time} · {p.revisions} {de ? "Korrekturen" : "revisions"}</div>
                <button onClick={() => start(p.id)} className={`mt-4 w-full ${p.recommended ? "btn-primary" : "btn-ghost"}`} data-testid={`package-cta-${i}`}>{pickL(p, "cta") || t("common.getStarted")}</button>
              </div>
            </Reveal>
          ))}
          {list.length === 0 && <p className="text-muted-foreground col-span-3 text-center py-10">{t("common.none")}</p>}
        </div>
        <p className="text-center text-sm text-muted-foreground mt-10">
          {de ? "Individuelles Projekt? " : "Custom project? "}
          <Link to="/quote" className="text-primary font-semibold underline">{t("hero.cta1")}</Link>
        </p>
      </section>
    </div>
  );
}
